import React, { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { matchPath, __RouterContext as RouterContext } from "react-router";
import { constants } from "utils/general";
import { trackViewedPage } from "utils/analytics";
import { useAuthUserDataContext } from "hooks/useAuthUserData/useAuthUserData";

const PAGE_ROUTES = [
  { path: constants.EDUCATOR_PATH, exact: true, pageName: "Dashboard" },
  { path: constants.GROUP_CODE_STANDARD_SEARCH_PATH, exact: false, pageName: "Group Code View Edit" },
  { path: constants.SESSION_HISTORY_PATH, exact: false, pageName: "Session History List", landingSentByPage: true },
  { path: constants.LIVE_SESSIONS_PATH, exact: false, pageName: "Live Sessions" },
  { path: constants.EDUCATOR_SETTINGS_PATH, exact: false, pageName: "Settings" },
  { path: constants.LESSONS_STANDARDS_PATH, exact: false, pageName: "Lessons & Standards" },
  { path: constants.VIEW_CHANGE_LESSON_PATH, exact: false, pageName: "Change Lesson" },
  { path: constants.VIEW_CHANGE_STANDARD_PATH, exact: false, pageName: "Change Standard" },
  { path: constants.WEEKLY_REPORTS_PATH, exact: false, pageName: "Weekly Reports" },
  { path: constants.STUDENT_REPORTS_PATH, exact: false, pageName: "Student Reports", landingSentByPage: true },
  // The page sends its own landing view, because only the page knows which tab
  // it opened on.
  { path: constants.CLASS_REPORTS_PATH, exact: false, pageName: "Class Reports", landingSentByPage: true },
  { path: constants.SCHOOL_REPORTS_PATH, exact: false, pageName: "School Reports", landingSentByPage: true },
  { path: constants.RESOURCE_HUB_PATH, exact: false, pageName: "Resource Hub" },
  { path: constants.QUESTION_SET_PATH, exact: false, pageName: "Question Set" },
  { path: constants.CREATE_QUICK_GROUP_PATH, exact: false, pageName: "Create Quick Group" },
  { path: constants.EDIT_GROUPS_PATH, exact: false, pageName: "Edit Groups" },
  // Sent by the page, so a flag-off visit that is redirected sends nothing.
  { path: constants.WARM_UP_PICKER_PATH, exact: false, pageName: "Warm-Up Challenges", landingSentByPage: true },
  // Both group-focus routes show the same label, so they share a page name and
  // are told apart by the same assignment_type the click events carry.
  {
    path: constants.EDIT_GROUP_FOCUS_PATH,
    exact: false,
    pageName: "Edit Group Focus",
    extras: { assignment_type: "Standard" },
  },
  {
    path: constants.LESSON_EDIT_GROUP_FOCUS_PATH,
    exact: false,
    pageName: "Edit Group Focus",
    extras: { assignment_type: "Lesson" },
  },
];

const matchRoute = (pathname) =>
  PAGE_ROUTES.find(({ path, exact }) => matchPath(pathname, { path, exact })) || null;

export const matchPageName = (pathname) => matchRoute(pathname)?.pageName ?? null;

// The location of whatever page a shared panel, sidebar or results list is
// rendered on. Read off the router context rather than useLocation, which
// throws where there is no router (these components' own unit tests render
// them bare); null there.
export const useHostLocation = () => useContext(RouterContext)?.location ?? null;

export const useHostPageName = () => {
  const location = useHostLocation();
  return location ? matchPageName(location.pathname) : null;
};

// Students and not-yet-identified teachers send no page views.
const canTrackViewsFor = (authUserData) =>
  Boolean(authUserData?.okoUserId) && !authUserData?.isAnonymous;

// For the views a page sends itself (its landing, its tabs, its panels), under
// the same guard as the route views.
export const useViewedPageTracking = () => {
  const { authUserData } = useAuthUserDataContext() || {};
  const canTrack = canTrackViewsFor(authUserData);
  const trackView = useCallback(
    (pageName, extras) => {
      if (canTrack) trackViewedPage(pageName, extras);
    },
    [canTrack]
  );
  return { canTrack, trackView };
};

// Sends a page's landing view once, as soon as the viewer is known and the
// page says it is ready (it may need data before it knows its extras).
export const useLandingPageView = (pageName, extras, { ready = true } = {}) => {
  const { canTrack, trackView } = useViewedPageTracking();
  const sentRef = useRef(false);
  const extrasRef = useRef(extras);
  extrasRef.current = extras;

  useEffect(() => {
    if (!canTrack || !ready || sentRef.current) return;
    sentRef.current = true;
    trackView(pageName, extrasRef.current);
  }, [canTrack, ready, pageName, trackView]);
};

const PanelViewMemoryContext = createContext(null);

// A page whose detail panel unmounts while search results show (and mounts
// again on the way back) wraps itself in this, so the panel's last item outlives
// the panel: coming back to the same item is not a new view.
export const withPanelViewMemory = (Page) => {
  const PageWithPanelViewMemory = (props) => {
    const lastShownItemRef = useRef(null);
    return (
      <PanelViewMemoryContext.Provider value={lastShownItemRef}>
        <Page {...props} />
      </PanelViewMemoryContext.Provider>
    );
  };
  return PageWithPanelViewMemory;
};

// A detail panel's view: once per item it shows (a lesson, a standard), not
// once per render. A null item sends nothing.
export const usePanelFirstView = (itemId, sendView) => {
  const { canTrack } = useViewedPageTracking();
  const pageMemoryRef = useContext(PanelViewMemoryContext);
  const panelMemoryRef = useRef(null);
  const sentForRef = pageMemoryRef || panelMemoryRef;
  useEffect(() => {
    if (!canTrack || !itemId || sentForRef.current === itemId) return;
    sentForRef.current = itemId;
    sendView();
  });
};

export const PageViewTracker = () => {
  const location = useLocation();
  const { authUserData } = useAuthUserDataContext() || {};
  const canTrack = canTrackViewsFor(authUserData);
  const lastTrackedRef = useRef(null);

  useEffect(() => {
    if (!canTrack) return;
    const route = matchRoute(location.pathname);
    if (!route || route.landingSentByPage) return;
    const dedupeKey = `${route.pageName}|${location.pathname}`;
    if (lastTrackedRef.current === dedupeKey) return;
    lastTrackedRef.current = dedupeKey;
    trackViewedPage(route.pageName, route.extras);
  }, [location.pathname, canTrack]);

  return null;
};
